// frontend/src/pages/org/org-team-page.tsx
// Read DESIGN.md before modifying this file.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useForm } from '@tanstack/react-form';
import {
  type ColumnDef,
  type Row,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { z } from 'zod';
import { Check, ChevronDown, Copy, Loader2, MoreVertical, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { FieldError } from '@/components/form/field-error';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { RoleGate } from '@/components/auth/role-gate';
import { orgUrl } from '@/lib/org-url';
import { RedactedEmail } from '@/components/redacted-email';
import { emailSchema } from '@/lib/form/schemas';
import {
  useInviteTeamMember,
  useOrgTeam,
  useOrgTeamWorkspaceOptions,
  useRemoveTeamMember,
  type OrgTeamWorkspaceOption,
  type TeamMemberRow,
} from './org-team-queries';
import { PageContainer } from '@/components/page-container';

const inviteSchema = z.object({
  email: z.string().trim().pipe(emailSchema),
  role: z.enum(['member', 'admin']),
  inviteWorkspaces: z.array(
    z.object({
      publicId: z.string(),
      name: z.string(),
    }),
  ),
});

type TeamFilterState = {
  typeFilter: 'all' | 'team' | 'workspace_scoped';
  roleFilter: 'all' | 'admin' | 'member';
  workspaceFilter: string;
  search: string;
};

function errorMessage(body: unknown, fallback: string): string {
  return body && typeof body === 'object' && 'userMessage' in body
    ? ((body as { userMessage?: string }).userMessage ?? fallback)
    : fallback;
}

function teamGlobalFilter(
  row: Row<TeamMemberRow>,
  _columnId: string,
  value: unknown,
): boolean {
  const filters = value as TeamFilterState;
  const member = row.original;

  if (filters.typeFilter !== 'all' && member.source !== filters.typeFilter) {
    return false;
  }
  if (filters.roleFilter !== 'all' && member.role !== filters.roleFilter) {
    return false;
  }
  if (
    filters.workspaceFilter !== 'all' &&
    !member.workspaces.some((w) => w.publicId === filters.workspaceFilter)
  ) {
    return false;
  }

  const q = filters.search.trim().toLowerCase();
  if (!q) return true;

  return `${member.displayName ?? ''} ${member.email} ${member.phone ?? ''}`
    .toLowerCase()
    .includes(q);
}

/** Same inline copy affordance as super-admin/users-table.tsx. */
function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="icon-xs"
      className="shrink-0"
      aria-label={`Copy ${label}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          toast.success(`${label} copied`);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error(`Failed to copy ${label}`);
        }
      }}
    >
      {copied ? (
        <Check className="size-3.5 text-emerald-600" />
      ) : (
        <Copy className="size-3.5" />
      )}
    </Button>
  );
}

function TypeBadge({ source }: { source: TeamMemberRow['source'] }) {
  // Same visual language as the workspace Members page KindBadge.
  return source === 'team' ? (
    <Badge variant="secondary">Team</Badge>
  ) : (
    <Badge variant="outline">Workspace</Badge>
  );
}

function TeamAccessPlaceholder() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Admin access required</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Only organization admins can view the Team roster.
        </p>
      </CardContent>
    </Card>
  );
}

function TeamContent() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const teamQuery = useOrgTeam(orgId);

  // Filter bar state. Users are filtered client-side: org rosters are small
  // and the endpoint already carries source + workspace assignments per row.
  const [typeFilter, setTypeFilter] = useState<'all' | 'team' | 'workspace_scoped'>('all');
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'member'>('all');
  const [workspaceFilter, setWorkspaceFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  // Add User (team invite) dialog state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmailError, setInviteEmailError] = useState<string | null>(null);
  const [wsFilter, setWsFilter] = useState('');

  const [removingId, setRemovingId] = useState<string | null>(null);
  const workspaceOptionsQuery = useOrgTeamWorkspaceOptions(orgId, inviteOpen);
  const inviteTeamMember = useInviteTeamMember(orgId);
  const removeTeamMember = useRemoveTeamMember(orgId);
  const { mutate: removeTeamMemberMutate } = removeTeamMember;

  const members = useMemo(
    () => teamQuery.data?.members ?? [],
    [teamQuery.data?.members],
  );

  const orgWorkspaceOptions = useMemo<OrgTeamWorkspaceOption[]>(
    () =>
      (workspaceOptionsQuery.data?.workspaces ?? []).map((w) => ({
        publicId: w.publicId,
        name: w.name,
      })),
    [workspaceOptionsQuery.data?.workspaces],
  );

  useEffect(() => {
    if (teamQuery.error) {
      toast.error(errorMessage(teamQuery.error, 'Failed to load the team roster'));
    }
  }, [teamQuery.error]);

  function validateInviteEmail(value: string): string | null {
    const parsedEmail = emailSchema.safeParse(value.trim());
    return parsedEmail.success
      ? null
      : (parsedEmail.error.issues[0]?.message ?? 'Enter a valid email address');
  }

  const inviteForm = useForm({
    defaultValues: {
      email: '',
      // AIT-531 — team invites are colleagues who need full org access, so the
      // select opens on Admin. Downgrading to Member is one click and still
      // reveals the assign-workspaces picker.
      role: 'admin' as 'member' | 'admin',
      inviteWorkspaces: [] as OrgTeamWorkspaceOption[],
    },
    validators: { onChange: inviteSchema, onSubmit: inviteSchema },
    onSubmit: async ({ value }) => {
      if (!orgId) return;
      const parsedEmail = emailSchema.safeParse(value.email.trim());
      if (!parsedEmail.success) {
        setInviteEmailError(validateInviteEmail(value.email));
        return;
      }

      inviteTeamMember.mutate(
        {
          email: parsedEmail.data,
          role: value.role,
          ...(value.role === 'member' && value.inviteWorkspaces.length > 0
            ? {
                workspacePublicIds: value.inviteWorkspaces.map(
                  (w) => w.publicId,
                ),
              }
            : {}),
        },
        {
          onSuccess: () => {
            toast.success('Invite sent');
            inviteForm.reset();
            setInviteOpen(false);
          },
          onError: (err) => {
            toast.error(errorMessage(err, 'Failed to send the invite'));
          },
        },
      );
    },
  });

  // Workspace filter options come from the data itself: a workspace with no
  // members can't match any row, so it has nothing to filter.
  const workspaceOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const m of members) {
      for (const w of m.workspaces) byId.set(w.publicId, w.name);
    }
    return [...byId.entries()].map(([publicId, name]) => ({ publicId, name }));
  }, [members]);

  const teamFilterState = useMemo<TeamFilterState>(
    () => ({ typeFilter, roleFilter, workspaceFilter, search }),
    [roleFilter, search, typeFilter, workspaceFilter],
  );

  const handleRemove = useCallback((row: TeamMemberRow) => {
    setRemovingId(row.publicId);
    removeTeamMemberMutate(
      {
        memberId: row.publicId,
        workspacePublicIds: row.workspaces.map((ws) => ws.publicId),
      },
      {
        onSuccess: () => {
          toast.success('Member removed');
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Failed to remove member'));
        },
        onSettled: () => setRemovingId(null),
      },
    );
  }, [removeTeamMemberMutate]);

  const columns = useMemo<ColumnDef<TeamMemberRow>[]>(
    () => [
      {
        accessorKey: 'displayName',
        header: 'Name',
        cell: ({ row }) => (
          <span className="flex min-w-0 items-center gap-1">
            <span className="truncate">{row.original.displayName ?? '-'}</span>
            {row.original.displayName && (
              <CopyButton value={row.original.displayName} label="Name" />
            )}
          </span>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => (
          <span className="flex min-w-0 items-center gap-1">
            <RedactedEmail email={row.original.email} />
            <CopyButton value={row.original.email} label="Email" />
          </span>
        ),
      },
      {
        accessorKey: 'source',
        header: 'Type',
        cell: ({ row }) => <TypeBadge source={row.original.source} />,
      },
      {
        accessorKey: 'role',
        header: 'Role',
        cell: ({ row }) => <span className="capitalize">{row.original.role}</span>,
      },
      {
        accessorKey: 'workspaces',
        header: 'Workspaces',
        cell: ({ row }) => {
          const { workspaces } = row.original;
          if (workspaces.length === 0) {
            return <span className="text-muted-foreground">-</span>;
          }

          return (
            <span className="flex items-center gap-1">
              {/* Fixed-width name slot so the +N badge lands at the same x on
                  every row; the badge hugs its truncated content within the slot. */}
              <span className="flex w-32 min-w-0">
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Badge
                        variant="outline"
                        className="min-w-0 max-w-full cursor-default"
                      />
                    }
                  >
                    <span className="truncate">{workspaces[0].name}</span>
                  </TooltipTrigger>
                  <TooltipContent>{workspaces[0].name}</TooltipContent>
                </Tooltip>
              </span>
              {workspaces.length > 1 && (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Badge variant="outline" className="shrink-0 cursor-default" />
                    }
                  >
                    +{workspaces.length - 1}
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <ul className="space-y-0.5">
                      {workspaces.slice(1, 13).map((w) => (
                        <li key={w.publicId}>{w.name}</li>
                      ))}
                      {workspaces.length - 1 > 12 && (
                        <li className="text-muted-foreground">
                          and {workspaces.length - 13} more
                        </li>
                      )}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              )}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: () => null,
        cell: ({ row }) => (
          <span className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Actions for ${
                      row.original.displayName ?? row.original.email
                    }`}
                  />
                }
              >
                {removingId === row.original.publicId ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <MoreVertical className="size-3.5" />
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-36">
                {row.original.source === 'team' && orgId && (
                  <DropdownMenuItem
                    onClick={() =>
                      navigate(`${orgUrl(orgId, 'team')}/${row.original.publicId}`)
                    }
                  >
                    <Pencil className="size-3.5" />
                    Edit
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  variant="destructive"
                  disabled={removingId !== null}
                  onClick={() => handleRemove(row.original)}
                >
                  <Trash2 className="size-3.5" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </span>
        ),
        enableGlobalFilter: false,
      },
    ],
    [handleRemove, navigate, orgId, removingId],
  );

  const table = useReactTable({
    data: members,
    columns,
    getRowId: (row) => row.publicId,
    state: {
      globalFilter: teamFilterState,
    },
    globalFilterFn: teamGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });
  const tableRows = table.getRowModel().rows;

  return (
    <>
      {/* Page header: title left, Add User action right (inline). */}
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Team</h1>
        <Dialog
          open={inviteOpen}
          onOpenChange={(open) => {
            if (!open) {
              inviteForm.reset();
              setInviteEmailError(null);
              setWsFilter('');
            }
            setInviteOpen(open);
          }}
        >
          <DialogTrigger
            render={
              <Button size="sm" className="h-9">
                <Plus className="size-3.5" />
                Add User
              </Button>
            }
          />
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add User</DialogTitle>
            </DialogHeader>
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void inviteForm.handleSubmit();
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="team-invite-email">Email</Label>
                <inviteForm.Field
                  name="email"
                  validators={{ onChange: emailSchema, onSubmit: emailSchema }}
                >
                  {(field) => {
                    const hasServerError = inviteEmailError !== null;
                    const hasError = hasServerError || field.state.meta.errors.length > 0;
                    return (
                      <>
                        <Input
                          id="team-invite-email"
                          type="email"
                          value={field.state.value}
                          onChange={(e) => {
                            if (inviteEmailError) setInviteEmailError(null);
                            field.handleChange(e.target.value);
                          }}
                          onBlur={field.handleBlur}
                          placeholder="teammate@example.com"
                          aria-describedby={
                            hasError ? 'team-invite-email-error' : undefined
                          }
                          aria-invalid={hasError || undefined}
                        />
                        <FieldError
                          id="team-invite-email-error"
                          errors={
                            hasServerError
                              ? [inviteEmailError]
                              : field.state.meta.errors
                          }
                        />
                      </>
                    );
                  }}
                </inviteForm.Field>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="team-invite-role">Role</Label>
                <inviteForm.Field name="role">
                  {(field) => (
                    <Select
                      value={field.state.value}
                      onValueChange={(v) =>
                        field.handleChange(v as 'member' | 'admin')
                      }
                    >
                      <SelectTrigger id="team-invite-role" className="w-28">
                        {/* Base UI renders the raw value, so the trigger read a
                            lowercase 'member'/'admin'. Map it to the item label. */}
                        <SelectValue>
                          {(value) => (value === 'admin' ? 'Admin' : 'Member')}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </inviteForm.Field>
              </div>
              <inviteForm.Subscribe
                selector={(state) => ({
                  role: state.values.role,
                  inviteWorkspaces: state.values.inviteWorkspaces,
                })}
              >
                {({ role, inviteWorkspaces }) =>
                  role === 'member' ? (
                    <inviteForm.Field name="inviteWorkspaces">
                      {(field) => {
                        const visibleWorkspaceOptions = orgWorkspaceOptions.filter((w) =>
                          w.name
                            .toLowerCase()
                            .includes(wsFilter.trim().toLowerCase()),
                        );

                        return (
                          <div className="space-y-2">
                            <Label>Assign To Workspaces (optional)</Label>
                            <DropdownMenu
                              onOpenChange={(open) => !open && setWsFilter('')}
                            >
                              <DropdownMenuTrigger
                                render={
                                  <Button
                                    variant="outline"
                                    aria-label="Assign workspaces"
                                    className="h-auto min-h-9 w-full justify-between font-normal"
                                  >
                                    {inviteWorkspaces.length === 0 ? (
                                      <span className="text-muted-foreground">
                                        Select workspaces
                                      </span>
                                    ) : orgWorkspaceOptions.length > 0 &&
                                      inviteWorkspaces.length ===
                                        orgWorkspaceOptions.length ? (
                                      <span>All workspaces</span>
                                    ) : (
                                      <span className="flex flex-wrap gap-1">
                                        {inviteWorkspaces.map((w) => (
                                          <Badge
                                            key={w.publicId}
                                            variant="secondary"
                                          >
                                            {w.name}
                                          </Badge>
                                        ))}
                                      </span>
                                    )}
                                    <ChevronDown className="ml-auto size-4 shrink-0 text-muted-foreground" />
                                  </Button>
                                }
                              />
                              <DropdownMenuContent className="w-[var(--anchor-width)] p-0">
                                <div className="flex items-center gap-2 border-b px-2 py-1.5">
                                  <Search className="size-4 shrink-0 text-muted-foreground" />
                                  <input
                                    aria-label="Search workspaces"
                                    placeholder="Search workspaces"
                                    value={wsFilter}
                                    onChange={(e) => setWsFilter(e.target.value)}
                                    // Keep keystrokes in the input: stop the menu's
                                    // typeahead from hijacking them, and don't let it
                                    // close on the input's own key events.
                                    onKeyDown={(e) => e.stopPropagation()}
                                    className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                                  />
                                </div>
                                <div className="max-h-52 overflow-y-auto p-1">
                                  {!wsFilter.trim() &&
                                    orgWorkspaceOptions.length > 0 && (
                                      <>
                                        <DropdownMenuCheckboxItem
                                          checked={
                                            inviteWorkspaces.length ===
                                            orgWorkspaceOptions.length
                                          }
                                          onCheckedChange={(checked) =>
                                            field.handleChange(
                                              checked === true
                                                ? [...orgWorkspaceOptions]
                                                : [],
                                            )
                                          }
                                          closeOnClick={false}
                                        >
                                          All workspaces
                                        </DropdownMenuCheckboxItem>
                                        <div className="-mx-1 my-1 h-px bg-foreground/10" />
                                      </>
                                    )}
                                  {visibleWorkspaceOptions.map((w) => (
                                    <DropdownMenuCheckboxItem
                                      key={w.publicId}
                                      checked={inviteWorkspaces.some(
                                        (s) => s.publicId === w.publicId,
                                      )}
                                      onCheckedChange={(checked) =>
                                        field.handleChange(
                                          checked === true
                                            ? [...inviteWorkspaces, w]
                                            : inviteWorkspaces.filter(
                                                (x) => x.publicId !== w.publicId,
                                              ),
                                        )
                                      }
                                      closeOnClick={false}
                                    >
                                      {w.name}
                                    </DropdownMenuCheckboxItem>
                                  ))}
                                  {visibleWorkspaceOptions.length === 0 && (
                                    <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                                      No workspaces match your search
                                    </p>
                                  )}
                                </div>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <p className="text-xs text-muted-foreground">
                              Invites a staff member and assigns the selected
                              workspaces. Leave empty to assign later.
                            </p>
                          </div>
                        );
                      }}
                    </inviteForm.Field>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Admins can access all workspaces.
                    </p>
                  )
                }
              </inviteForm.Subscribe>
              <inviteForm.Subscribe
                selector={(state) => ({
                  email: state.values.email,
                  isSubmitting: state.isSubmitting,
                })}
              >
                {({ email, isSubmitting }) => (
                  <Button
                    type="button"
                    onClick={(e) => {
                      const message = validateInviteEmail(email);
                      if (message) {
                        e.preventDefault();
                        setInviteEmailError(message);
                        return;
                      }
                      void inviteForm.handleSubmit();
                    }}
                    disabled={
                      isSubmitting ||
                      inviteTeamMember.isPending ||
                      !String(email).trim()
                    }
                    className="w-full"
                  >
                    {inviteTeamMember.isPending && (
                      <Loader2 className="size-3.5 animate-spin" />
                    )}
                    Send Invite
                  </Button>
                )}
              </inviteForm.Subscribe>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* GHL-style filter bar: user type, role, workspace, search. */}
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={typeFilter}
          onValueChange={(v) => setTypeFilter(v as typeof typeFilter)}
        >
          <SelectTrigger className="h-9 w-[8.5rem]" aria-label="User Type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="team">Team</SelectItem>
            <SelectItem value="workspace_scoped">Workspace</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={roleFilter}
          onValueChange={(v) => setRoleFilter(v as typeof roleFilter)}
        >
          <SelectTrigger className="h-9 w-[7.5rem]" aria-label="User Role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="member">Member</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={workspaceFilter}
          onValueChange={(v) => setWorkspaceFilter(v ?? 'all')}
        >
          <SelectTrigger className="h-9 w-[10.5rem]" aria-label="Workspace">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Workspaces</SelectItem>
            {workspaceOptions.map((w) => (
              <SelectItem key={w.publicId} value={w.publicId}>
                {w.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative min-w-40 flex-1">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name or email"
            className="h-9 pl-8 text-sm"
          />
        </div>
      </div>

      {/* No flex-1 on the Card: it hugs its rows and only shrinks (min-h-0)
          when they would overflow the viewport, scrolling the table
          internally. */}
      <Card className="min-h-0 !overflow-visible">
        <CardContent className="flex min-h-0 flex-1 flex-col">
          <div className="scrollable-table min-h-0 flex-1 [&_[data-slot=table-container]]:h-full">
            <Table className="table-fixed max-md:min-w-[44rem]">
              <colgroup>
                <col style={{ width: '22%' }} />
                <col style={{ width: '28%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <TableHeader className="sticky top-0 z-10 bg-card pointer-events-none">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {tableRows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {tableRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground">
                      No members match the current filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

    </>
  );
}

/**
 * Team page (spec Decisions 2/4, operator revision 2026-06-12): the full org
 * roster with type/role/workspace/search filters (search covers name, email,
 * and phone), team invites (Add User), and per-row actions — the pencil opens
 * the member page (/org/:orgId/team/:memberId) where role, profile, and
 * workspace assignments are managed. Org-admin only. The nav item is already
 * hidden for non-admins (OrgSidebar); the RoleGate covers deep links.
 */
export function OrgTeamPage() {
  return (
    <PageContainer variant="fill" className="gap-4">
      <RoleGate
        role="admin"
        fallback={
          <>
            <h1 className="text-xl font-semibold">Team</h1>
            <TeamAccessPlaceholder />
          </>
        }
      >
        <TeamContent />
      </RoleGate>
    </PageContainer>
  );
}
